/**
 * Ritual Bath Repository
 *
 * Data access layer for Ritual Baths CRUD operations.
 * All API requests use axiosClient with credentials for authentication.
 */

import { axiosClient } from "../api/axiosClient";
import type {
  AddRitualBathOccurrencePayload,
  AddRitualBathOccurrenceResponse,
  BathCategory,
  CopyRitualBathPayload,
  CreateBathCategoryPayload,
  CreateRitualBathPayload,
  PaginatedRitualBathResponse,
  RitualBath,
  RitualBathListParams,
  UpdateBathCategoryPayload,
  UpdateRitualBathPayload,
} from "../types/ritualBath.types";

// ============================================================================
// Query Key Helpers
// ============================================================================

function toRitualBathListQueryParams(params?: RitualBathListParams) {
  if (!params) {
    return undefined;
  }

  const { limit, page, categoryId, origin, ...rest } = params;

  return {
    ...rest,
    page,
    perPage: limit,
    origin,
    categoryId: categoryId != null ? String(categoryId) : undefined,
  };
}

export const ritualBathQueryKeys = {
  all: ["ritual-baths"] as const,
  lists: () => [...ritualBathQueryKeys.all, "list"] as const,
  list: (params?: RitualBathListParams) =>
    [...ritualBathQueryKeys.lists(), params] as const,
  system: () => [...ritualBathQueryKeys.all, "system"] as const,
  my: () => [...ritualBathQueryKeys.all, "my"] as const,
  byTerreiro: (terreiroId: string) =>
    [...ritualBathQueryKeys.all, "terreiro", terreiroId] as const,
  byMedium: (mediumId: string) =>
    [...ritualBathQueryKeys.all, "medium", mediumId] as const,
  detail: (id: string) => [...ritualBathQueryKeys.all, "detail", id] as const,
  categories: {
    all: ["ritual-bath-categories"] as const,
    list: (terreiroId?: string) =>
      [...ritualBathQueryKeys.categories.all, "list", terreiroId ?? null] as const,
    detail: (id: string) =>
      [...ritualBathQueryKeys.categories.all, "detail", id] as const,
  },
} as const;

// ============================================================================
// Repository
// ============================================================================

export const ritualBathRepository = {
  // --------------------------------------------------------------------------
  // Ritual Bath CRUD
  // --------------------------------------------------------------------------

  /**
   * List ritual baths with optional filters and pagination
   */
  async listRitualBaths(
    params?: RitualBathListParams
  ): Promise<PaginatedRitualBathResponse> {
    const { data } = await axiosClient.get<PaginatedRitualBathResponse>(
      "/ritual-baths",
      {
        params: toRitualBathListQueryParams(params),
        withCredentials: true,
      }
    );
    return data;
  },

  /**
   * Get all system ritual baths (origin = SYSTEM)
   */
  async getSystemRitualBaths(): Promise<PaginatedRitualBathResponse> {
    const { data } = await axiosClient.get<PaginatedRitualBathResponse>(
      "/ritual-baths/system",
      {
        withCredentials: true,
      }
    );
    return data;
  },

  /**
   * Get the current user's ritual baths (origin = MEDIUM, mediumId = current user)
   */
  async getMyRitualBaths(
    params?: RitualBathListParams,
  ): Promise<PaginatedRitualBathResponse> {
    const { data } = await axiosClient.get<PaginatedRitualBathResponse>(
      "/ritual-baths/my",
      {
        params: toRitualBathListQueryParams(params),
        withCredentials: true,
      },
    );
    return data;
  },

  /**
   * Get a single ritual bath by ID
   */
  async getRitualBathById(id: string): Promise<RitualBath> {
    const { data } = await axiosClient.get<RitualBath>(`/ritual-baths/${id}`, {
      withCredentials: true,
    });
    return data;
  },

  /**
   * Get ritual baths by terreiro ID
   */
  async getRitualBathsByTerreiro(
    terreiroId: string,
    params?: RitualBathListParams,
  ): Promise<PaginatedRitualBathResponse> {
    const { data } = await axiosClient.get<PaginatedRitualBathResponse>(
      `/ritual-baths/terreiro/${terreiroId}`,
      {
        params: toRitualBathListQueryParams(params),
        withCredentials: true,
      },
    );
    return data;
  },

  /**
   * Get ritual baths by medium ID
   */
  async getRitualBathsByMedium(mediumId: string): Promise<RitualBath[]> {
    const { data } = await axiosClient.get<RitualBath[]>(
      `/ritual-baths/medium/${mediumId}`,
      {
        withCredentials: true,
      }
    );
    return data;
  },

  /**
   * Create a new ritual bath
   */
  async createRitualBath(
    payload: CreateRitualBathPayload
  ): Promise<RitualBath> {
    const { data } = await axiosClient.post<RitualBath>(
      "/ritual-baths",
      payload,
      {
        withCredentials: true,
      }
    );
    return data;
  },

  /**
   * Update an existing ritual bath
   */
  async updateRitualBath(
    id: string,
    payload: Partial<UpdateRitualBathPayload>
  ): Promise<RitualBath> {
    const { data } = await axiosClient.put<RitualBath>(
      `/ritual-baths/${id}`,
      payload,
      {
        withCredentials: true,
      }
    );
    return data;
  },

  /**
   * Upload (or replace) the ritual bath photo
   */
  async uploadRitualBathImage(id: string, file: File): Promise<RitualBath> {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await axiosClient.patch<RitualBath>(
      `/ritual-baths/${id}/image`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      }
    );
    return data;
  },

  /**
   * Copy a ritual bath to a different origin
   */
  async copyRitualBath(payload: CopyRitualBathPayload): Promise<RitualBath> {
    const { data } = await axiosClient.post<RitualBath>(
      "/ritual-baths/copy",
      payload,
      {
        withCredentials: true,
      }
    );
    return data;
  },

  /**
   * Delete a ritual bath by ID
   */
  async deleteRitualBath(id: string): Promise<void> {
    await axiosClient.delete(`/ritual-baths/${id}`, {
      withCredentials: true,
    });
  },

  async addRitualBathOccurrence(
    bathId: string,
    payload: AddRitualBathOccurrencePayload,
  ): Promise<AddRitualBathOccurrenceResponse> {
    const { data } = await axiosClient.post<AddRitualBathOccurrenceResponse>(
      `/ritual-baths/${bathId}/occurrences`,
      payload,
      { withCredentials: true },
    );
    return data;
  },

  // --------------------------------------------------------------------------
  // Bath Category CRUD
  // --------------------------------------------------------------------------

  /**
   * List system bath categories plus the terreiro's own when `terreiroId` is given
   */
  async listBathCategories(terreiroId?: string): Promise<BathCategory[]> {
    const { data } = await axiosClient.get<BathCategory[]>(
      "/ritual-baths/categories",
      {
        params: terreiroId ? { terreiroId } : undefined,
        withCredentials: true,
      }
    );
    return data;
  },

  /**
   * Get a single bath category by ID
   */
  async getBathCategoryById(id: string): Promise<BathCategory> {
    const { data } = await axiosClient.get<BathCategory>(
      `/ritual-baths/categories/${id}`,
      {
        withCredentials: true,
      }
    );
    return data;
  },

  /**
   * Create a new bath category
   */
  async createBathCategory(
    payload: CreateBathCategoryPayload
  ): Promise<BathCategory> {
    const { data } = await axiosClient.post<BathCategory>(
      "/ritual-baths/categories",
      payload,
      {
        withCredentials: true,
      }
    );
    return data;
  },

  /**
   * Update an existing bath category
   */
  async updateBathCategory(
    id: string,
    payload: Partial<UpdateBathCategoryPayload>
  ): Promise<BathCategory> {
    const { data } = await axiosClient.put<BathCategory>(
      `/ritual-baths/categories/${id}`,
      payload,
      {
        withCredentials: true,
      }
    );
    return data;
  },

  /**
   * Delete a bath category by ID
   */
  async deleteBathCategory(id: string): Promise<void> {
    await axiosClient.delete(`/ritual-baths/categories/${id}`, {
      withCredentials: true,
    });
  },
};
